"use client";

import React, { Suspense } from "react";

import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { useTranslations } from "next-intl";

import { type Locale } from "@/shared.i18n/config";
import { cn } from "@/shared-web.ui/util.cn";

import {
  type ServerDataOutput,
  useSubscriptionSelectorActions,
} from "../data.subscription-data";
import * as SelectorContainer from "../prim.selector-container";
import { useCarouselParams } from "./hook.use-carousel-params";
import {
  ArrowButton,
  usePrevNextButtons,
} from "./ui.arrow-button";
import { DotButton, useDotButton } from "./ui.dot-button";
import { PackageCard } from "./ui.package-card";

type PropType = {
  data: ServerDataOutput;
  locale: Locale;
  options?: EmblaOptionsType;
  className?: string;
  onChange?: (v: string) => void;
  value?: string;
};

export const SelectorPackageCarousel = (props: PropType) => {
  const { data, options, className, locale, onChange, value } = props;

  const {
    subscriptionData: {
      subscriptionMeta: {
        packageMeta,
      },
      sampleCoffees,
    },
    subscriptionVariant: {
      subscriptionPackageVariant,
    },
    stickerPriceData: {
      packageStartStickerPrice,
      packageStartBoxSize,
    },
  } = data;

  const optionsKeyList = subscriptionPackageVariant.variantOptionsArray;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...options,
  });

  useCarouselParams({
    data,
    emblaApi,
    value,
    onChange,
  });

  const { selectedIndex, onDotButtonClick }
    = useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <section className={cn("flex w-full flex-col gap-4", className)}>
      <div className={`
        grid grid-cols-[1fr_auto] justify-between gap-rx3
        p-1
      `}
      >
        <div className={`
          flex flex-wrap items-center justify-start gap-2
        `}
        >
          {optionsKeyList.map(({ optionKey }, index) => {
            const optionMeta = packageMeta[optionKey];
            return (
              <DotButton
                key={index}
                onClick={() => { return onDotButtonClick(index); }}
                isActive={index === selectedIndex}
              >
                {optionMeta.emoji}
              </DotButton>
            );
          })}
        </div>
        <div className={`
          grid grid-cols-2 items-center gap-2
        `}
        >
          <ArrowButton
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            arrow="prev"
          />
          <ArrowButton
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            arrow="next"
          />
        </div>

      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className={`
          flex touch-pan-y touch-pinch-zoom items-stretch
        `}
        >
          {optionsKeyList.map(({ optionKey }, index) => {
            const d = subscriptionPackageVariant.variantOptions[optionKey];
            const optionMeta = packageMeta[optionKey];
            const startAtPrice = packageStartStickerPrice[optionKey] || 0;
            const startingSize = packageStartBoxSize[optionKey];

            return (
              <div
                className={`
                  min-w-0 flex-none basis-full transform-gpu
                  p-1
                `}
                key={index}
              >
                <div className={cn(
                  `
                    size-full select-none rounded-2 border
                    border-gray-6
                  `,
                  index === selectedIndex && `
                    outline outline-2 outline-offset-2
                    outline-accent-8
                  `,
                )}
                >
                  <PackageCard
                    sampleCoffees={sampleCoffees[optionKey] || []}
                    emoji={optionMeta.emoji}
                    title={d.optionName}
                    tasteNotes={optionMeta.tasteNotes[locale]}
                    startingPrice={startAtPrice}
                    startingSize={startingSize}
                    description={optionMeta.description[locale]}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

interface PackageSelectorDisplayProps {
  data: ServerDataOutput;
  locale: Locale;
  onChange?: (v: string) => void;
  value?: string;
}
const PackageSelectorDisplay = (props: PackageSelectorDisplayProps) => {
  const { data, locale, onChange, value } = props;

  const t = useTranslations("stf_m_subscription.screen_subscription.package_selector");

  return (
    <SelectorContainer.Root>
      <SelectorContainer.Title>
        {t("choose_your_package")}
      </SelectorContainer.Title>
      <SelectorContainer.Body disablePadding>
        <SelectorPackageCarousel
          data={data}
          locale={locale}
          onChange={onChange}
          value={value}
        />
      </SelectorContainer.Body>
    </SelectorContainer.Root>
  );
};

interface PackageSelectorWithParamsProps {
  data: ServerDataOutput;
  locale: Locale;
}
const PackageSelectorWithParams = (props: PackageSelectorWithParamsProps) => {
  const { data, locale } = props;

  const { setVal, getVal } = useSubscriptionSelectorActions({ data });

  return (
    <PackageSelectorDisplay
      data={data}
      locale={locale}
      value={getVal("subscriptionPackage")}
      onChange={(v) => {
        setVal("subscriptionPackage", v);
      }}
    />
  );
};

interface SelectorPackageProps {
  data: ServerDataOutput;
  locale: Locale;
}
export const SelectorPackage = (props: SelectorPackageProps) => {
  const { data, locale } = props;

  return (
    <Suspense fallback={<PackageSelectorDisplay data={data} locale={locale} />}>
      <PackageSelectorWithParams data={data} locale={locale} />
    </Suspense>
  );
};
